import { useEffect, useState } from 'react'

import classnames from 'classnames'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'

import { COMMENT_MAX_LENGTH } from './constants'
import { ThankYouFormProps, FormData } from './types'
import schema from './schema'

import Toast from 'common/components/toast'
import Button, { Variant } from 'common/components/button'
import Icons from 'common/components/icons'
import FormRate from 'modules/transmission/components/rate/formRate'
import TransmissionService from 'modules/transmission/service'
import Textarea from 'common/components/form/textarea'
import { useLeadContext } from 'modules/lead/context'
import Loading from 'common/components/loading'

import './thankYouForm.scss'
import '@hotmart/cosmos/dist/styles/utilities/sizing/sizing.css'
import '@hotmart/cosmos/dist/styles/utilities/display/display.css'
import '@hotmart/cosmos/dist/styles/utilities/flex/flex.css'

const ThankYouForm = ({ transmissionCode }: ThankYouFormProps) => {
  const { postLead } = useLeadContext()
  const { response: { contactId, email, name } } = postLead

  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [checkedAnswered, setCheckedAnswered] = useState<boolean>(false)
  const [answered, setAnswered] = useState<boolean>(false)
  const [sendingFeedback, setSendingFeedback] = useState<boolean>(false)

  const { handleSubmit, register, control, watch } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onBlur'
  })

  const currentRate = watch('rate')

  const sendFeedback = async (rate: number, comment?: string) => {
    if (contactId) {
      try {
        setSendingFeedback(true)
        await TransmissionService.saveFeedback({
          email,
          feedback: comment,
          leadCode: contactId,
          name,
          ratingStar: rate,
          scheduleCode: transmissionCode
        })
        setAnswered(true)
      } catch (error) {
        Toast.danger(error?.message)
        console.error(error)
      } finally {
        setSendingFeedback(false)
      }
    }
  }

  useEffect(() => {
    const fetchFeedback = async () => {
      if (contactId) {
        try {
          setIsLoading(true)

          const feedback = await TransmissionService.getFeedback(
            transmissionCode,
            contactId
          )

          setAnswered(Boolean(feedback))
        } catch (error) {
          console.error(error)
        } finally {
          setCheckedAnswered(true)
          setIsLoading(false)
        }
      }
    }

    fetchFeedback()
  }, [transmissionCode, contactId])

  const onFormSubmit = ({ rate, comment }: FormData) => {
    if (!answered && rate) {
      sendFeedback(rate, comment)
    }
  }

  const renderForm = () => (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      data-test="form">
      <div className="rate-container">
        <FormRate
          label={t('thank-form.form.fields.rate.label')}
          control={control}
          invalidFeedback={t('general.field-required')}
          disabled={!checkedAnswered || sendingFeedback}/>
      </div>
      <Textarea
        {...register('comment')}
        className="_w-full"
        label={t('thank-form.form.fields.comment.label')}
        rows={4}
        maxLength={COMMENT_MAX_LENGTH}
        placeholder={t('thank-form.form.fields.comment.placeholder')}
        disabled={!checkedAnswered || sendingFeedback}/>
      <Button
        type="submit"
        className="_w-full"
        variant={Variant.PRIMARY}
        disabled={!currentRate || !checkedAnswered}
        loading={sendingFeedback}>
        {t('thank-form.form.submit')}
      </Button>
    </form>
  )

  const renderRateSent = () => (
    <div className="rate-sent-card">
      <Icons prefix="fas" iconName="check-circle" />
      <span className="rate-sent-message">{t('thank-form.sent.title')}</span>
      <span className="rate-sent-details">{t('thank-form.sent.subtitle')}</span>
    </div>
  )

  const renderContent = () => {
    if (isLoading || postLead.isLoading) {
      return <Loading className="_d-flex _justify-content-center" />
    }

    return answered ? renderRateSent() : renderForm()
  }

  return (
    <section className={classnames('thank-you-form', { 'is-sent': answered })}>
      {renderContent()}
    </section>
  )
}

export default ThankYouForm
